import React, { useEffect, useState, useCallback } from 'react';
import { X, Loader2, Link2, Unlink, Plus, ExternalLink, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from './IntelligenceLayout';
import { JobOrderStageBadge } from './CRMJobOrders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONFIDENCE_COLORS = { high: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-gray-100 text-gray-600' };

const CRMJobOrderDetail = ({ jobOrderId, config, onClose, onUpdated }) => {
  const [jobOrder, setJobOrder] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showAddRevenue, setShowAddRevenue] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/crm/job-orders/${jobOrderId}`).then(d => { setJobOrder(d); setForm(d); }).catch(() => {}).finally(() => setLoading(false));
  }, [jobOrderId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/job-orders/${jobOrderId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_title: form.job_title, location: form.location, work_mode: form.work_mode,
          comp_min: form.comp_min ? Number(form.comp_min) : null, comp_max: form.comp_max ? Number(form.comp_max) : null,
          comp_frequency: form.comp_frequency, employment_type: form.employment_type, priority: form.priority, status: form.status,
          must_have_requirements: form.must_have_requirements, nice_to_have_requirements: form.nice_to_have_requirements,
          target_fill_date: form.target_fill_date, num_openings: Number(form.num_openings || 1), fee_percentage: Number(form.fee_percentage),
          notes: form.notes, next_action: form.next_action, next_action_due_date: form.next_action_due_date,
        }),
      });
      toast.success('Job Order updated');
      load(); onUpdated && onUpdated();
    } catch (e) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const findManatalMatches = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/crm/job-orders/${jobOrderId}/manatal-suggestions`);
      setSuggestions(res);
    } catch (e) {
      toast.error('Could not fetch Manatal suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const confirmLink = async (s) => {
    if (!window.confirm(`Confirm linking to Manatal Job #${s.manatal_job_id} (${s.job_title})? This only stores a reference — nothing is written to Manatal.`)) return;
    try {
      await fetchWithAuth(`${API_URL}/api/crm/job-orders/${jobOrderId}/manatal-link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manatal_job_id: s.manatal_job_id, manatal_job_url: null }),
      });
      toast.success('Linked to Manatal job');
      load();
    } catch (e) {
      toast.error('Link failed');
    }
  };

  const unlink = async () => {
    try {
      await fetchWithAuth(`${API_URL}/api/crm/job-orders/${jobOrderId}/manatal-link`, { method: 'DELETE' });
      toast.success('Unlinked');
      load();
    } catch (e) {
      toast.error('Unlink failed');
    }
  };

  if (loading || !form) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" data-testid="job-order-detail-modal">
        <Loader2 className="h-6 w-6 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-end" data-testid="job-order-detail-modal">
      <div className="bg-white h-full w-full max-w-xl shadow-xl overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#0A1F44]">{jobOrder.job_title}</h3>
            <p className="text-xs text-gray-500">{jobOrder.company_name}</p>
          </div>
          <button onClick={onClose} data-testid="close-job-order-detail"><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        <div className="flex items-center gap-2">
          <JobOrderStageBadge status={jobOrder.status} />
          {jobOrder.hiring_contact && <span className="text-xs text-gray-500">Hiring contact: {jobOrder.hiring_contact.first_name} {jobOrder.hiring_contact.last_name}</span>}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <input value={form.job_title} onChange={e => setForm({ ...form, job_title: e.target.value })} data-testid="jo-detail-title" placeholder="Job title" className="col-span-2 px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input value={form.location || ''} onChange={e => setForm({ ...form, location: e.target.value })} data-testid="jo-detail-location" placeholder="Location" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <select value={form.work_mode || ''} onChange={e => setForm({ ...form, work_mode: e.target.value })} data-testid="jo-detail-work-mode" className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
            {config.work_modes.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
          <input type="number" value={form.comp_min || ''} onChange={e => setForm({ ...form, comp_min: e.target.value })} data-testid="jo-detail-comp-min" placeholder="Comp min" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input type="number" value={form.comp_max || ''} onChange={e => setForm({ ...form, comp_max: e.target.value })} data-testid="jo-detail-comp-max" placeholder="Comp max" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <select value={form.comp_frequency || 'Annual'} onChange={e => setForm({ ...form, comp_frequency: e.target.value })} data-testid="jo-detail-comp-frequency" className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
            {config.comp_frequencies.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <input type="number" value={form.fee_percentage || ''} onChange={e => setForm({ ...form, fee_percentage: e.target.value })} data-testid="jo-detail-fee-percentage" placeholder="Fee %" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <select value={form.priority || ''} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="jo-detail-priority" className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
            {config.priorities.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={form.status || ''} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="jo-detail-status" className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
            {config.stages.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input type="number" value={form.num_openings || 1} onChange={e => setForm({ ...form, num_openings: e.target.value })} data-testid="jo-detail-openings" placeholder="# Openings" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input type="date" value={form.target_fill_date || ''} onChange={e => setForm({ ...form, target_fill_date: e.target.value })} data-testid="jo-detail-target-fill-date" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        </div>
        <p className="text-xs text-gray-500">Estimated Fee: <b className="text-[#0A1F44]">{form.estimated_fee ? `$${Math.round(form.estimated_fee.min).toLocaleString()}–$${Math.round(form.estimated_fee.max).toLocaleString()}` : 'Not available (missing or non-annual compensation)'}</b></p>

        <textarea value={form.must_have_requirements || ''} onChange={e => setForm({ ...form, must_have_requirements: e.target.value })} data-testid="jo-detail-must-haves" placeholder="Must-have requirements" rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <textarea value={form.nice_to_have_requirements || ''} onChange={e => setForm({ ...form, nice_to_have_requirements: e.target.value })} data-testid="jo-detail-nice-to-haves" placeholder="Nice-to-have requirements" rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.next_action || ''} onChange={e => setForm({ ...form, next_action: e.target.value })} data-testid="jo-detail-next-action" placeholder="Next action" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input type="date" value={form.next_action_due_date || ''} onChange={e => setForm({ ...form, next_action_due_date: e.target.value })} data-testid="jo-detail-next-action-due" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        </div>
        <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="jo-detail-notes" placeholder="Notes" rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <button onClick={save} disabled={saving} data-testid="save-job-order-detail" className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>

        <div className="border-t border-gray-100 pt-4 space-y-2" data-testid="manatal-link-section">
          <h4 className="text-sm font-semibold text-[#0A1F44]">Manatal Job Link</h4>
          {jobOrder.manatal_job_id ? (
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Linked to Manatal Job #{jobOrder.manatal_job_id}
                {jobOrder.manatal_job_url && <a href={jobOrder.manatal_job_url} target="_blank" rel="noreferrer" className="ml-1"><ExternalLink className="h-3 w-3" /></a>}
              </span>
              <button onClick={unlink} data-testid="unlink-manatal-job-btn" className="text-xs text-red-600 flex items-center gap-1 hover:underline"><Unlink className="h-3 w-3" /> Unlink</button>
            </div>
          ) : (
            <>
              <button onClick={findManatalMatches} disabled={loadingSuggestions} data-testid="find-manatal-matches-btn"
                className="text-sm px-3 py-2 border border-gray-200 rounded-lg text-[#0A1F44] hover:border-[#D4AF37]/50 flex items-center gap-1.5">
                {loadingSuggestions ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Find likely Manatal jobs
              </button>
              {suggestions && !suggestions.connected && <p className="text-xs text-amber-600" data-testid="manatal-not-connected">{suggestions.message || 'Manatal is not connected.'}</p>}
              {suggestions?.connected && suggestions.suggestions.length === 0 && (
                <p className="text-xs text-gray-400" data-testid="no-manatal-suggestions">No likely matches found in Manatal — you can still leave this job order unlinked, or check Manatal directly.</p>
              )}
              {suggestions?.connected && suggestions.suggestions.map(s => (
                <div key={s.manatal_job_id} className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2" data-testid={`manatal-suggestion-${s.manatal_job_id}`}>
                  <div>
                    <p className="text-sm font-medium text-[#0A1F44]">{s.job_title} <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${CONFIDENCE_COLORS[s.confidence]}`}>{s.confidence}</span></p>
                    <p className="text-xs text-gray-500">{s.organization_name || 'Unknown org'} · {[s.city, s.country].filter(Boolean).join(', ') || 'No location'} · {s.reasons.join(', ')}</p>
                  </div>
                  <button onClick={() => confirmLink(s)} data-testid={`confirm-link-${s.manatal_job_id}`} className="text-xs px-2.5 py-1.5 bg-[#0A1F44] text-white rounded-lg hover:bg-[#0A1F44]/90">Confirm Link</button>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-2" data-testid="job-order-revenue-section">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#0A1F44]">Revenue / Placements</h4>
            <button onClick={() => setShowAddRevenue(true)} data-testid="add-revenue-record-btn" className="text-xs text-[#0A1F44] flex items-center gap-1 hover:underline"><Plus className="h-3 w-3" /> Add</button>
          </div>
          {(jobOrder.revenue_records || []).length === 0 ? (
            <p className="text-xs text-gray-400">No revenue records yet for this job order.</p>
          ) : jobOrder.revenue_records.map(r => (
            <div key={r.id} className="border border-gray-200 rounded-lg px-3 py-2 text-xs" data-testid={`jo-revenue-record-${r.id}`}>
              <p className="font-medium text-[#0A1F44]">{r.candidate_name || 'Unnamed placement'} — {r.invoice_status}</p>
              <p className="text-gray-500">Actual fee ${r.actual_fee?.toLocaleString() || '—'} · Invoiced ${r.amount_invoiced?.toLocaleString() || '0'} · Collected ${r.amount_collected?.toLocaleString() || '0'}</p>
            </div>
          ))}
        </div>
      </div>

      {showAddRevenue && (
        <AddRevenueModal jobOrder={jobOrder} onClose={() => setShowAddRevenue(false)} onCreated={() => { setShowAddRevenue(false); load(); }} />
      )}
    </div>
  );
};

const AddRevenueModal = ({ jobOrder, onClose, onCreated }) => {
  const [form, setForm] = useState({ candidate_name: '', placement_date: '', actual_fee: '', invoice_status: 'Not Ready' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/revenue`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, job_order_id: jobOrder.id, actual_fee: form.actual_fee ? Number(form.actual_fee) : null, agreed_fee_percentage: jobOrder.fee_percentage }),
      });
      toast.success('Revenue record added');
      onCreated();
    } catch (e) {
      toast.error('Could not add revenue record');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="add-revenue-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-bold text-[#0A1F44]">Add Revenue Record</h3>
          <button onClick={onClose} data-testid="close-add-revenue-modal"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <input value={form.candidate_name} onChange={e => setForm({ ...form, candidate_name: e.target.value })} placeholder="Candidate name"
          data-testid="revenue-candidate-name-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <input type="date" value={form.placement_date} onChange={e => setForm({ ...form, placement_date: e.target.value })}
          data-testid="revenue-placement-date-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <input type="number" value={form.actual_fee} onChange={e => setForm({ ...form, actual_fee: e.target.value })} placeholder="Actual placement fee"
          data-testid="revenue-actual-fee-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <button onClick={submit} disabled={saving} data-testid="add-revenue-submit"
          className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Adding...' : 'Add Record'}
        </button>
      </div>
    </div>
  );
};

export default CRMJobOrderDetail;
